import { type ThunkAction } from "redux-thunk";
import { type RootState } from "../../redux/store";
import { loadingSuccessAction, type NavigationActions } from "./actions";
import { type NavigationData } from "../jspComponents/Navigation";
import { toMapFn } from "../../utils/reducers";
import { getNextUID } from "../../utils/uid";
import { type NavigationNode, type NavigationNodeDTO } from "../types";
import { CURRENT_LANGUAGE } from "../../utils/constants";
import { resolveUrl } from "~/utils/url/urlResolver";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, NavigationActions>;

const STORAGE_NAVIGATION_KEY = "cache.navigation";

interface FullNavDTO {
  navigation: string;
  flyoutNavigation: string;
}

export interface FullNavData extends NavigationData {
  flyoutNodes: NavigationNode[];
}

export function loadNavigationEpic(dto: FullNavDTO): ThunkResult<void> {
  return (dispatch) => {
    let data: FullNavData | null = null;

    // Case 1 - Data from BE
    if (dto.navigation) {
      data = transform(dto);
      // if (data) {
      //   commitToCache(data);
      // }
    }

    // Case 2 -  Data from Cache
    // if (!data && window.sessionStorage && getCookie(STORAGE_NAVIGATION_KEY) === CURRENT_LANGUAGE) {
    //   const storedData = window.sessionStorage.getItem(STORAGE_NAVIGATION_KEY);
    //   if (storedData) {
    //     data = JSON.parse(storedData);
    //   }
    // }

    // Case 3 - Fallback, fetch() the navigation
    // if (!data) {
    //   invalidateNavigationCache();
    //   dispatch(beginLoadingAction());
    //
    //   fetch("/")
    //     .then((res) => res.text())
    //     .then((text) => {
    //       const parser = new DOMParser();
    //       const doc = parser.parseFromString(text, "text/html");
    //       const node = doc.querySelector("#root_Navigation script");
    //       const scriptText = node?.innerHTML.trim() ?? "";
    //
    //       const navigation = extractJSONString(scriptText, "const navigation");
    //       const flyoutNavigation = extractJSONString(scriptText, "const flyoutNavigation");
    //
    //       data = transform({ navigation, flyoutNavigation });
    //
    //       if (data) {
    //         commitToCache(data);
    //         dispatch(loadingSuccessAction(data));
    //       }
    //     });
    // }

    if (data) {
      dispatch(loadingSuccessAction(data));
    }
  };
}

function extractJSONString(script: string, lineMarker: string) {
  const dataRowIndex = script.indexOf(lineMarker);
  const startIndex = script.indexOf("'", dataRowIndex);
  const endIndex = script.indexOf(";\n", dataRowIndex);

  const rawData = script.substring(startIndex + 1, endIndex - 1);
  return rawData.replace(/\\"/g, '"').replace(/\\\//g, "/");
}

function commitToCache(data: FullNavData) {
  if (window.sessionStorage) {
    requestAnimationFrame(() => {
      window.sessionStorage.setItem(STORAGE_NAVIGATION_KEY, JSON.stringify(data));
      setCookie({ name: STORAGE_NAVIGATION_KEY, value: CURRENT_LANGUAGE });
    });
  }
}

export function invalidateNavigationCache() {
  window.sessionStorage.removeItem(STORAGE_NAVIGATION_KEY);
  deleteCookie({ name: STORAGE_NAVIGATION_KEY });
}

interface CookieProps {
  name: string;
  value?: string;
  path?: string;
  domain?: string;
  maxAge?: string;
  expires?: string;
}

function setCookie({
  name,
  value = "",
  path = "/",
  domain = document.location.hostname,
  maxAge,
  expires,
}: CookieProps) {
  let cookie = `${name}=${value};path=${path};domain=${domain};`;
  if (maxAge) {
    cookie += `max-age=${maxAge};`;
  }
  if (expires) {
    cookie += `expires=${expires};`;
  }
  document.cookie = cookie;
}

function deleteCookie(data: Pick<CookieProps, "name" | "path" | "domain">) {
  setCookie({ ...data, expires: "Thu, 01 Jan 1970 00:00:01 GMT" });
}

function transform({ navigation, flyoutNavigation }: FullNavDTO): FullNavData | null {
  if (!navigation) {
    return null;
  }

  try {
    const navData = transformNavigationNodes(JSON.parse(navigation) || []);

    let flyoutNodes: NavigationNode[] = [];
    if (flyoutNavigation) {
      try {
        const data = transformNavigationNodes(JSON.parse(flyoutNavigation) || []);
        flyoutNodes = data.root.children.map((id) => data.nodes[id]);
        flyoutNodes.forEach((node) => (node.children = []));
      } catch (e) {
        console.error("Error when parsing flyout navigation");
        console.error(e);
      }
    }

    return { ...navData, flyoutNodes };
  } catch (e) {
    console.error("Error parsing nav data");
  }

  return null;
}

function transformNavigationNodes(dto: NavigationNodeDTO[]): NavigationData {
  const root: NavigationNode = {
    id: getNextUID(),
    gaLabel: "",
    title: "",
    level: -1,
    openInNewTab: false,
    children: [],
    usefulTools: [],
    showSeriesTree: false,
  };

  const level0Nodes = dto.map((node) => transformSingle(node, 0));
  root.children = level0Nodes.map(({ node }) => node.id);

  const nodes = toMapFn(flattenChildren(level0Nodes), (node) => node.id);

  return { root, nodes };
}

type TransformSingleResult = { node: NavigationNode; allSubNodes: NavigationNode[] };

function transformSingle(dto: NavigationNodeDTO, level: number): TransformSingleResult {
  const children = dto.children.map((node) => transformSingle(node, level + 1));

  let bigImage = undefined;
  if (dto.bigImage) {
    bigImage = {
      ...dto.bigImage,
      id: getNextUID(),
    };
  }

  let smallImage = undefined;
  if (dto.smallImage) {
    smallImage = {
      ...dto.smallImage,
      id: getNextUID(),
    };
  }

  const node: NavigationNode = {
    ...dto,
    url: dto.url ? resolveUrl(dto.url) : undefined,
    openInNewTab: dto.openInNewTab ?? false,
    showSeriesTree: dto.showSeriesTree ?? false,
    level,
    id: getNextUID(),
    bigImage,
    smallImage,
    children: children.map((child) => child.node.id),
    usefulTools: dto.usefulTools ?? [],
    pimCategoryNode: dto.pimCategoryNode ?? false,
  };

  const allSubNodes = flattenChildren(children);

  return { node, allSubNodes };
}

const flattenChildren = (children: TransformSingleResult[]) =>
  children.map(({ node, allSubNodes }) => [node, ...allSubNodes]).flat();

export function getCookie(name: string): string {
  const cookies = document.cookie.split(";").reduce((map, cookie) => {
    const [key, val] = cookie.trim().split("=");
    map[key] = val;
    return map;
  }, {} as Dict<string>);
  return cookies[name] ?? "";
}
